/**
 * Organic family renderers — 10 approved generators.
 * Ported faithfully from generative-backgrounds-v3.jsx reference.
 *
 * All renderers share the signature:
 *   render(ctx, W, H, palette, params, noiseEngine)
 *
 * Where:
 *   ctx       — Canvas2D context
 *   W, H      — canvas dimensions
 *   palette   — { bg, colors, rgbColors } (already adjusted)
 *   params    — { scale, density, complexity }
 *   noiseEngine — from createNoiseEngine(seed)
 */

import { lerp } from '../noise';

// ═══════════════════════════════════════════════════════════
//  1. CIRCLE PACK — pressure-packed circles + rain streaks
// ═══════════════════════════════════════════════════════════

export function renderCirclePack(ctx, W, H, pal, P, engine) {
  const { density = 0.5, scale = 0.5, complexity = 3 } = P;
  const { fbm, rng } = engine;
  const cols = pal.colors;

  ctx.fillStyle = pal.bg; ctx.fillRect(0, 0, W, H);

  // Stone-paved grid base
  const gridSize = Math.floor(20 + (1 - scale) * 40);
  const nCols = Math.ceil(W / gridSize) + 1;
  const nRows = Math.ceil(H / gridSize) + 1;
  ctx.lineWidth = 0.5;
  for (let r = 0; r < nRows; r++) {
    for (let c = 0; c < nCols; c++) {
      const jx = (rng() - 0.5) * gridSize * 0.3;
      const jy = (rng() - 0.5) * gridSize * 0.3;
      const x = c * gridSize + jx, y = r * gridSize + jy;
      const sz = gridSize * (0.7 + rng() * 0.5);
      ctx.strokeStyle = cols[0]; ctx.globalAlpha = 0.08;
      ctx.strokeRect(x - sz / 2, y - sz / 2, sz, sz);
    }
  }

  // Circle packing
  const circles = [];
  const maxAttempts = Math.floor(2000 + density * 8000);
  const minR = 3, maxR = 15 + scale * 50;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const cx = rng() * W, cy = rng() * H;
    const pressure = fbm(cx * 0.002, cy * 0.002, Math.floor(complexity) + 1) * 0.5 + 0.5;
    const r = minR + pressure * (maxR - minR) * rng();
    let valid = true;
    for (const other of circles) {
      const dx = cx - other.x, dy = cy - other.y;
      if (dx * dx + dy * dy < (r + other.r + 2) * (r + other.r + 2)) { valid = false; break; }
    }
    if (!valid) continue;
    circles.push({ x: cx, y: cy, r, pressure });
  }

  for (const c of circles) {
    const ci = Math.floor(c.pressure * (cols.length - 1));
    ctx.fillStyle = cols[ci]; ctx.globalAlpha = 0.15 + c.pressure * 0.4;
    ctx.beginPath(); ctx.arc(c.x, c.y, c.r, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = cols[Math.min(ci + 1, cols.length - 1)]; ctx.lineWidth = 0.5;
    ctx.globalAlpha = 0.2 + c.pressure * 0.3; ctx.stroke();
  }

  // Rain streaks
  ctx.lineWidth = 0.5; ctx.lineCap = 'round';
  const rainDrops = Math.floor(200 + density * 1500);
  for (let i = 0; i < rainDrops; i++) {
    const x = rng() * W, startY = rng() * H * 0.6;
    let cloudiness = 0;
    for (const c of circles) {
      const dx = x - c.x, dy = startY - c.y;
      if (dx * dx + dy * dy < c.r * c.r * 4) cloudiness += 0.3;
    }
    if (rng() > Math.min(1, cloudiness)) continue;
    const len = 20 + rng() * 80;
    const ci = Math.floor(rng() * cols.length);
    ctx.strokeStyle = cols[ci]; ctx.globalAlpha = 0.05 + rng() * 0.15;
    ctx.lineWidth = 0.3 + rng() * 1;
    ctx.beginPath(); ctx.moveTo(x, startY); ctx.lineTo(x + (rng() - 0.5) * 5, startY + len); ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

// ═══════════════════════════════════════════════════════════
//  2. STIPPLE — noise-driven pointillism
// ═══════════════════════════════════════════════════════════

export function renderStipple(ctx, W, H, pal, P, engine) {
  const { density = 0.5, scale = 0.5, complexity = 3 } = P;
  const { fbm, rng } = engine;
  const cols = pal.colors;
  const count = Math.floor(5000 + density * 40000);
  const maxR = 1 + scale * 5;
  const oct = Math.max(2, Math.floor(complexity) + 1);

  ctx.fillStyle = pal.bg; ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < count; i++) {
    const x = rng() * W, y = rng() * H;
    const n = fbm(x * 0.002, y * 0.002, oct) * 0.5 + 0.5;
    const n2 = fbm(x * 0.005 + 100, y * 0.005 + 100, 3) * 0.5 + 0.5;
    if (rng() > n * 0.7 + 0.3) continue;
    ctx.fillStyle = cols[Math.floor(n * (cols.length - 1))];
    ctx.globalAlpha = 0.2 + n * 0.6;
    ctx.beginPath(); ctx.arc(x, y, 0.3 + n2 * maxR, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ═══════════════════════════════════════════════════════════
//  3. EROSION — terrain with eroded valleys and ridges
// ═══════════════════════════════════════════════════════════

export function renderErosion(ctx, W, H, pal, P, engine) {
  const { scale = 0.5, complexity = 3, density = 0.5 } = P;
  const { fbm, ridged } = engine;
  const rgbs = pal.rgbColors;
  const ns = 0.001 + scale * 0.004;
  const oct = Math.max(2, Math.floor(complexity) + 1);
  const img = ctx.createImageData(W, H);
  const d = img.data;

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      let h = fbm(x * ns, y * ns, oct) * 0.5 + 0.5;
      const r = ridged(x * ns * 1.5, y * ns * 1.5, oct);
      h = lerp(h, r, 0.4 + density * 0.3);
      const hR = fbm((x + 2) * ns, y * ns, oct) * 0.5 + 0.5;
      const hD = fbm(x * ns, (y + 2) * ns, oct) * 0.5 + 0.5;
      const slope = Math.sqrt((h - hR) ** 2 + (h - hD) ** 2) * 15;
      let val = lerp(h, Math.min(1, slope), 0.3);
      val = Math.max(0, Math.min(1, val));
      const ci = val * (rgbs.length - 1);
      const lo = Math.floor(ci), hi = Math.min(lo + 1, rgbs.length - 1), t = ci - lo;
      const idx = (y * W + x) * 4;
      d[idx]     = Math.round(lerp(rgbs[lo][0], rgbs[hi][0], t));
      d[idx + 1] = Math.round(lerp(rgbs[lo][1], rgbs[hi][1], t));
      d[idx + 2] = Math.round(lerp(rgbs[lo][2], rgbs[hi][2], t));
      d[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
}

// ═══════════════════════════════════════════════════════════
//  4. SMOKE — ethereal smoky pixel displacement
// ═══════════════════════════════════════════════════════════

export function renderSmoke(ctx, W, H, pal, P, engine) {
  const { scale = 0.5, complexity = 3, density = 0.5 } = P;
  const { fbm } = engine;
  const rgbs = pal.rgbColors;
  const ns = 0.0015 + scale * 0.005;
  const oct = Math.max(2, Math.floor(complexity) + 1);
  const layers = Math.floor(3 + density * 5);
  const img = ctx.createImageData(W, H);
  const d = img.data;

  // Background fill
  const bgRgb = pal.rgbColors[0] || [8, 8, 24];
  // Use palette bg color by parsing it
  const bgMatch = pal.bg.match(/\d+/g);
  const bgR = bgMatch ? parseInt(bgMatch[0]) : 8;
  const bgG = bgMatch ? parseInt(bgMatch[1]) : 8;
  const bgB = bgMatch ? parseInt(bgMatch[2]) : 8;
  for (let i = 0; i < W * H * 4; i += 4) {
    d[i] = bgR; d[i + 1] = bgG; d[i + 2] = bgB; d[i + 3] = 255;
  }

  for (let layer = 0; layer < layers; layer++) {
    const layerOffset = layer * 37.7;
    const intensity = 0.1 + (layer / layers) * 0.3;
    const colIdx = Math.floor((layer / layers) * (rgbs.length - 1));
    const col = rgbs[colIdx];

    for (let y = 0; y < H; y++) {
      for (let x = 0; x < W; x++) {
        const dx = fbm(x * ns + layerOffset, y * ns, oct) * 150;
        const dy = fbm(x * ns + layerOffset + 50, y * ns + 50, oct) * 150;
        const sx = x + dx, sy = y + dy;
        let smokeVal = fbm(sx * ns * 2, sy * ns * 2, oct);
        smokeVal = smokeVal * 0.5 + 0.5;
        const cx = W / 2, cy = H / 2;
        const distFromCenter = Math.sqrt((x - cx) ** 2 + (y - cy) ** 2) / (Math.max(W, H) * 0.5);
        smokeVal *= Math.max(0, 1 - distFromCenter * 0.5);
        const alpha = smokeVal * intensity * 255;
        if (alpha < 1) continue;
        const idx = (y * W + x) * 4;
        d[idx]     = Math.min(255, d[idx] + col[0] * alpha / 255);
        d[idx + 1] = Math.min(255, d[idx + 1] + col[1] * alpha / 255);
        d[idx + 2] = Math.min(255, d[idx + 2] + col[2] * alpha / 255);
      }
    }
  }
  ctx.putImageData(img, 0, 0);
}

// ═══════════════════════════════════════════════════════════
//  5. PARTICLES — connected particle constellation
// ═══════════════════════════════════════════════════════════

export function renderParticles(ctx, W, H, pal, P, engine) {
  const { density = 0.5, scale = 0.5, complexity = 3 } = P;
  const { fbm, rng } = engine;
  const cols = pal.colors;
  const count = Math.floor(400 + density * 3000);
  const maxR = 1.5 + scale * 6;
  const connDist = 25 + complexity * 35;

  ctx.fillStyle = pal.bg; ctx.fillRect(0, 0, W, H);

  const particles = [];
  for (let i = 0; i < count; i++) {
    const x = rng() * W, y = rng() * H;
    const n = fbm(x * 0.002, y * 0.002, 3) * 0.5 + 0.5;
    particles.push({ x, y, r: 0.5 + n * maxR, ci: Math.floor(n * cols.length) % cols.length });
  }
  particles.sort((a, b) => a.x - b.x);

  ctx.lineWidth = 0.3;
  for (let i = 0; i < particles.length; i++) {
    for (let j = i + 1; j < particles.length; j++) {
      if (particles[j].x - particles[i].x > connDist) break;
      const dy = particles[i].y - particles[j].y;
      if (Math.abs(dy) > connDist) continue;
      const dd = Math.sqrt((particles[i].x - particles[j].x) ** 2 + dy * dy);
      if (dd < connDist) {
        ctx.strokeStyle = cols[particles[i].ci];
        ctx.globalAlpha = 0.04 * (1 - dd / connDist);
        ctx.beginPath();
        ctx.moveTo(particles[i].x, particles[i].y);
        ctx.lineTo(particles[j].x, particles[j].y);
        ctx.stroke();
      }
    }
  }

  for (const p of particles) {
    ctx.fillStyle = cols[p.ci];
    ctx.globalAlpha = 0.35 + rng() * 0.55;
    ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2); ctx.fill();
  }
  ctx.globalAlpha = 1;
}

// ═══════════════════════════════════════════════════════════
//  6. TOPOGRAPHY — contour elevation map
// ═══════════════════════════════════════════════════════════

export function renderTopography(ctx, W, H, pal, P, engine) {
  const { scale = 0.5, complexity = 3, density = 0.5 } = P;
  const { fbm } = engine;
  const cols = pal.colors;
  const ns = 0.001 + scale * 0.005;
  const levels = Math.floor(12 + density * 30);
  const oct = Math.max(1, Math.floor(complexity) + 1);

  ctx.fillStyle = pal.bg; ctx.fillRect(0, 0, W, H);

  const res = 2;
  const gW = Math.ceil(W / res), gH = Math.ceil(H / res);
  const grid = new Float32Array(gW * gH);
  for (let gy = 0; gy < gH; gy++) {
    for (let gx = 0; gx < gW; gx++) {
      grid[gy * gW + gx] = fbm(gx * res * ns, gy * res * ns, oct) * 0.5 + 0.5;
    }
  }

  for (let l = 0; l < levels; l++) {
    const thr = (l + 0.5) / levels;
    const ci = Math.floor((l / levels) * cols.length);
    ctx.strokeStyle = cols[Math.min(ci, cols.length - 1)];
    ctx.globalAlpha = 0.35 + (l / levels) * 0.55;
    ctx.lineWidth = 0.4 + (l / levels) * 1.8;
    ctx.beginPath();
    for (let gy = 0; gy < gH - 1; gy++) {
      for (let gx = 0; gx < gW - 1; gx++) {
        const v00 = grid[gy * gW + gx], v10 = grid[gy * gW + gx + 1];
        const v01 = grid[(gy + 1) * gW + gx], v11 = grid[(gy + 1) * gW + gx + 1];
        const x0 = gx * res, y0 = gy * res;
        const edges = [];
        if ((v00 >= thr) !== (v10 >= thr)) edges.push([x0 + ((thr - v00) / (v10 - v00)) * res, y0]);
        if ((v10 >= thr) !== (v11 >= thr)) edges.push([x0 + res, y0 + ((thr - v10) / (v11 - v10)) * res]);
        if ((v01 >= thr) !== (v11 >= thr)) edges.push([x0 + ((thr - v01) / (v11 - v01)) * res, y0 + res]);
        if ((v00 >= thr) !== (v01 >= thr)) edges.push([x0, y0 + ((thr - v00) / (v01 - v00)) * res]);
        if (edges.length >= 2) {
          ctx.moveTo(edges[0][0], edges[0][1]);
          ctx.lineTo(edges[1][0], edges[1][1]);
        }
      }
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

// ═══════════════════════════════════════════════════════════
//  7. DOMAIN WARP — multi-layer fbm marble
// ═══════════════════════════════════════════════════════════

export function renderDomainWarp(ctx, W, H, pal, P, engine) {
  const { scale = 0.5, complexity = 3 } = P;
  const { fbm } = engine;
  const rgbs = pal.rgbColors;
  const ns = 0.001 + scale * 0.004;
  const oct = Math.max(2, Math.floor(complexity) + 1);
  const img = ctx.createImageData(W, H);
  const d = img.data;

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const px = x * ns, py = y * ns;
      const qx = fbm(px, py, oct), qy = fbm(px + 5.2, py + 1.3, oct);
      const rx = fbm(px + 4 * qx + 1.7, py + 4 * qy + 9.2, oct);
      const ry = fbm(px + 4 * qx + 8.3, py + 4 * qy + 2.8, oct);
      let val = fbm(px + 4 * rx, py + 4 * ry, oct) * 0.5 + 0.5;
      const ci = val * (rgbs.length - 1);
      const lo = Math.floor(ci), hi = Math.min(lo + 1, rgbs.length - 1), t = ci - lo;
      const idx = (y * W + x) * 4;
      d[idx]     = Math.round(lerp(rgbs[lo][0], rgbs[hi][0], t));
      d[idx + 1] = Math.round(lerp(rgbs[lo][1], rgbs[hi][1], t));
      d[idx + 2] = Math.round(lerp(rgbs[lo][2], rgbs[hi][2], t));
      d[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
}

// ═══════════════════════════════════════════════════════════
//  8. RIDGED — luminescent ridgeline mountain bands ★ FAVORITE
// ═══════════════════════════════════════════════════════════

export function renderRidged(ctx, W, H, pal, P, engine) {
  const { scale = 0.5, complexity = 3 } = P;
  const { fbm, ridged } = engine;
  const rgbs = pal.rgbColors;
  const ns = 0.001 + scale * 0.004;
  const oct = Math.max(2, Math.floor(complexity) + 1);
  const img = ctx.createImageData(W, H);
  const d = img.data;

  for (let y = 0; y < H; y++) {
    for (let x = 0; x < W; x++) {
      const wx = fbm(x * ns * 0.5, y * ns * 0.5, 3) * 2;
      const wy = fbm(x * ns * 0.5 + 50, y * ns * 0.5 + 50, 3) * 2;
      let val = ridged(x * ns + wx, y * ns + wy, oct);
      val = Math.max(0, Math.min(1, val));
      const ci = val * (rgbs.length - 1);
      const lo = Math.floor(ci), hi = Math.min(lo + 1, rgbs.length - 1), t = ci - lo;
      const idx = (y * W + x) * 4;
      d[idx]     = Math.round(lerp(rgbs[lo][0], rgbs[hi][0], t));
      d[idx + 1] = Math.round(lerp(rgbs[lo][1], rgbs[hi][1], t));
      d[idx + 2] = Math.round(lerp(rgbs[lo][2], rgbs[hi][2], t));
      d[idx + 3] = 255;
    }
  }
  ctx.putImageData(img, 0, 0);
}

// ═══════════════════════════════════════════════════════════
//  9. SILK THREADS — delicate flowing threads ★ FAVORITE
// ═══════════════════════════════════════════════════════════

export function renderSilkThreads(ctx, W, H, pal, P, engine) {
  const { density = 0.5, scale = 0.5, complexity = 3 } = P;
  const { fbm, rng } = engine;
  const cols = pal.colors;
  const threads = Math.floor(500 + density * 3000);
  const ns = 0.001 + scale * 0.004;

  ctx.fillStyle = pal.bg; ctx.fillRect(0, 0, W, H);
  ctx.lineCap = 'round';

  for (let i = 0; i < threads; i++) {
    let x = rng() * W, y = -20 + rng() * (H + 40);
    const ci = Math.floor(rng() * cols.length);
    ctx.strokeStyle = cols[ci];
    ctx.lineWidth = 0.2 + rng() * 0.6;
    ctx.globalAlpha = 0.03 + rng() * 0.08;
    ctx.beginPath(); ctx.moveTo(x, y);
    const steps = Math.floor(100 + complexity * 100);
    for (let s = 0; s < steps; s++) {
      const angle = fbm(x * ns, y * ns, Math.floor(complexity) + 1) * Math.PI * 3;
      x += Math.cos(angle) * 3 + 0.5;
      y += Math.sin(angle) * 3;
      if (x > W + 20 || y < -30 || y > H + 30) break;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}

// ═══════════════════════════════════════════════════════════
//  10. FLUID — Navier-Stokes inspired brush strokes ★ FAVORITE
// ═══════════════════════════════════════════════════════════

export function renderFluid(ctx, W, H, pal, P, engine) {
  const { density = 0.5, scale = 0.5, complexity = 3 } = P;
  const { noise2, rng } = engine;
  const cols = pal.colors;
  const numParticles = Math.floor(200 + density * 800);
  const steps = Math.floor(80 + complexity * 80);
  const viscosity = 0.02 + (1 - scale) * 0.08;

  const nSources = Math.floor(3 + complexity * 3);
  const sources = [];
  for (let i = 0; i < nSources; i++) {
    sources.push({
      x: rng() * W, y: rng() * H,
      vx: (rng() - 0.5) * 6, vy: (rng() - 0.5) * 6,
      str: 0.5 + rng() * 2,
    });
  }

  ctx.fillStyle = pal.bg; ctx.fillRect(0, 0, W, H);
  ctx.lineCap = 'round';

  for (let i = 0; i < numParticles; i++) {
    let x = rng() * W, y = rng() * H;
    let vx = 0, vy = 0;
    const ci = Math.floor(rng() * cols.length);
    ctx.strokeStyle = cols[ci];
    ctx.lineWidth = 0.5 + rng() * 3;
    ctx.globalAlpha = 0.08 + rng() * 0.2;
    ctx.beginPath(); ctx.moveTo(x, y);

    for (let s = 0; s < steps; s++) {
      let ax = 0, ay = 0;
      for (const src of sources) {
        const dx = src.x - x, dy = src.y - y;
        const dist = Math.sqrt(dx * dx + dy * dy) + 1;
        const force = src.str / (dist * 0.01 + 1);
        ax += (-dy / dist * force * 0.7 + dx / dist * force * 0.3);
        ay += (dx / dist * force * 0.7 + dy / dist * force * 0.3);
      }
      const nAngle = noise2(x * 0.003, y * 0.003) * Math.PI * 2;
      ax += Math.cos(nAngle) * 0.5;
      ay += Math.sin(nAngle) * 0.5;
      vx = vx * (1 - viscosity) + ax * 0.15;
      vy = vy * (1 - viscosity) + ay * 0.15;
      x += vx; y += vy;
      if (x < -50 || x > W + 50 || y < -50 || y > H + 50) break;
      ctx.lineTo(x, y);
    }
    ctx.stroke();
  }
  ctx.globalAlpha = 1;
}
